/** ISO 8601 week helpers (Mon–Sun). Matches backend ScheduleByWeekService. */

export function mondayOfIsoWeek(year, week) {
    const jan4 = new Date(Date.UTC(year, 0, 4))
    const jan4Day = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay()
    const delta = 1 - jan4Day
    const week1Mon = new Date(jan4.getTime() + delta * 86400000)
    return new Date(week1Mon.getTime() + (week - 1) * 7 * 86400000)
}

export function sundayOfIsoWeek(year, week) {
    const mon = mondayOfIsoWeek(year, week)
    return new Date(mon.getTime() + 6 * 86400000)
}

export function isoWeeksInYear(year) {
    const jan1 = new Date(Date.UTC(year, 0, 1)).getUTCDay()
    const dec31 = new Date(Date.UTC(year, 11, 31)).getUTCDay()
    const isLeap =
        (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
    if (jan1 === 4 || dec31 === 4) return 53
    if (isLeap && (jan1 === 3 || dec31 === 3)) return 53
    return 52
}

export function formatIsoDate(date) {
    return date.toISOString().slice(0, 10)
}

export function formatWeekLabel(date) {
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

export function defaultWeekTitle(week, year) {
    return `WK ${week} / ${year} Audit Schedule`
}

export function buildWeekDropdownOptions(year) {
    const max = isoWeeksInYear(year)
    const options = []
    for (let week = 1; week <= max; week += 1) {
        const mon = mondayOfIsoWeek(year, week)
        const sun = sundayOfIsoWeek(year, week)
        options.push({
            value: week,
            text: `WK ${week} (${formatWeekLabel(mon)} → ${formatWeekLabel(sun)})`,
        })
    }
    return options
}

export function isoWeekAndYearFromDate(date) {
    if (!date) return { year: null, week: null }
    const d = new Date(date)
    const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
    const day = utc.getUTCDay() || 7
    utc.setUTCDate(utc.getUTCDate() + 4 - day)
    const year = utc.getUTCFullYear()
    const yearStart = new Date(Date.UTC(year, 0, 1))
    const week = Math.ceil(((utc - yearStart) / 86400000 + 1) / 7)
    return { year, week }
}

export function buildYearOptions() {
    const currentYear = new Date().getFullYear()
    return [currentYear - 1, currentYear, currentYear + 1].map(year => ({
        value: year,
        text: String(year),
    }))
}

export function buildMonthOptions() {
    return Array.from({ length: 12 }, (_, index) => {
        const month = index + 1
        const label = new Date(2000, index, 1).toLocaleDateString('en-US', {
            month: 'long',
        })
        return { value: month, text: label }
    })
}

/** Sun–Sat weeks for a calendar month. Each week is owned by the month of its Sunday (no overlap across months). */
export function getSundaySaturdayWeeksInMonth(year, month) {
    const monthIndex = month - 1
    const firstOfMonth = new Date(year, monthIndex, 1)
    const lastOfMonth = new Date(year, monthIndex + 1, 0)

    const weeks = []
    const cursor = new Date(firstOfMonth)
    cursor.setDate(cursor.getDate() - cursor.getDay())

    let weekOfMonth = 1
    while (cursor <= lastOfMonth) {
        const sunday = new Date(cursor)
        const saturday = new Date(cursor)
        saturday.setDate(saturday.getDate() + 6)

        if (
            sunday >= firstOfMonth &&
            sunday <= lastOfMonth
        ) {
            weeks.push({ weekOfMonth, sunday, saturday })
            weekOfMonth += 1
        }

        cursor.setDate(cursor.getDate() + 7)
    }

    return weeks
}

export function buildWeekOfMonthDropdownOptions(year, month) {
    return getSundaySaturdayWeeksInMonth(year, month).map(week => ({
        value: week.weekOfMonth,
        text: `Week ${week.weekOfMonth} (${formatWeekLabel(week.sunday)} → ${formatWeekLabel(week.saturday)})`,
        sunday: week.sunday,
        saturday: week.saturday,
    }))
}

/** Week rows for the entry-form grid — full Sun–Sat ranges (may cross month boundaries). */
export function buildWeekRowsForMonth(year, month) {
    return getSundaySaturdayWeeksInMonth(year, month).map(week => ({
        key: `${year}-${month}-${week.weekOfMonth}`,
        weekOfMonth: week.weekOfMonth,
        sunday: week.sunday,
        saturday: week.saturday,
        startDate: localDateToIso(week.sunday),
        endDate: localDateToIso(week.saturday),
        isPartial: false,
    }))
}

export function formatWeekDateRangeLabel(startDate, endDate) {
    const start = new Date(`${startDate}T00:00:00`)
    const end = new Date(`${endDate}T00:00:00`)
    const from = start.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    })
    const to = end.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    })
    return `${from} – ${to}`
}

export function defaultMonthWeekTitle(month, year, weekOfMonth) {
    const monthName = new Date(year, month - 1, 1).toLocaleDateString('en-US', {
        month: 'long',
    })
    return `${monthName} ${year} - Week ${weekOfMonth} Audit Schedule`
}

export function localDateToIso(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export function monthYearFromDate(date) {
    if (!date) return { month: null, year: null }
    const d = date instanceof Date ? date : new Date(date)
    if (Number.isNaN(d.getTime())) return { month: null, year: null }
    const iso = String(date).includes('T') ? String(date).slice(0, 10) : null
    if (iso) {
        const [year, month] = iso.split('-').map(Number)
        if (year && month) return { month, year }
    }
    return { month: d.getMonth() + 1, year: d.getFullYear() }
}

export function dateRangeForMonthYear(month, year) {
    const monthIndex = month - 1
    return {
        start: new Date(year, monthIndex, 1),
        end: new Date(year, monthIndex + 1, 0),
    }
}

export function rotationApiDateTime(isoDate) {
    if (!isoDate) return null
    return `${isoDate}T00:00:00`
}

export function isWeeklyRotationDetail(item) {
    return item?.DurationDays != null && item.DurationDays === 7
}

/** Sunday that starts the Sun–Sat week containing the given date. */
export function getSundayOfWeekContaining(dateValue) {
    const d = parseRotationApiDate(dateValue)
    if (!d) return null
    const sunday = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    sunday.setDate(sunday.getDate() - sunday.getDay())
    return sunday
}

/**
 * Viewer month filter — same ownership rule as the entry form:
 * Sun–Sat weeks belong to the month of their Sunday.
 */
export function rotationDetailOwnedByMonth(item, month, year) {
    if (!item?.FromDate || !month || !year) return false

    if (isWeeklyRotationDetail(item)) {
        const sunday = getSundayOfWeekContaining(item.FromDate)
        if (!sunday) return false
        return sunday.getMonth() + 1 === month && sunday.getFullYear() === year
    }

    const from = parseRotationApiDate(item.FromDate)
    const to = parseRotationApiDate(item.ToDate || item.FromDate)
    const { start, end } = dateRangeForMonthYear(month, year)
    if (!from || !to) return false
    return from <= end && to >= start
}

function parseRotationApiDate(value) {
    if (!value) return null
    const iso = String(value).slice(0, 10)
    const [year, month, day] = iso.split('-').map(Number)
    if (!year || !month || !day) return null
    return new Date(year, month - 1, day)
}

function weekRowBounds(row) {
    if (row?.startDate && row?.endDate) {
        return {
            start: new Date(`${row.startDate}T00:00:00`),
            end: new Date(`${row.endDate}T00:00:00`),
        }
    }
    if (row?.sunday && row?.saturday) {
        return {
            start: new Date(
                row.sunday.getFullYear(),
                row.sunday.getMonth(),
                row.sunday.getDate(),
            ),
            end: new Date(
                row.saturday.getFullYear(),
                row.saturday.getMonth(),
                row.saturday.getDate(),
            ),
        }
    }
    return null
}

function countInclusiveDays(start, end) {
    if (!start || !end || end < start) return 0
    return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1
}

function countRangeOverlap(startA, endA, startB, endB) {
    const overlapStart = new Date(
        Math.max(startA.getTime(), startB.getTime()),
    )
    const overlapEnd = new Date(Math.min(endA.getTime(), endB.getTime()))
    return countInclusiveDays(overlapStart, overlapEnd)
}

/**
 * Map saved rotation details onto Sun–Sat week rows by date-range overlap.
 * Each detail is used at most once; the week with the strongest overlap wins.
 */
export function matchRotationDetailsToWeekRows(baseRows, details) {
    const sorted = [...(details || [])].sort(
        (a, b) =>
            new Date(a.FromDate).getTime() - new Date(b.FromDate).getTime(),
    )

    if (!baseRows?.length || !sorted.length) {
        return baseRows?.map(() => null) || []
    }

    // Saved schedules are one detail per week in date order — safest for edit load.
    if (sorted.length === baseRows.length) {
        return sorted
    }

    const usedDetailIndexes = new Set()

    return baseRows.map(row => {
        const bounds = weekRowBounds(row)
        if (!bounds) return null

        const { start: weekStart, end: weekEnd } = bounds

        let bestDetail = null
        let bestDetailIndex = -1
        let bestScore = -1

        sorted.forEach((detail, index) => {
            if (usedDetailIndexes.has(index)) return

            const detailStart = parseRotationApiDate(detail.FromDate)
            const detailEnd = parseRotationApiDate(
                detail.ToDate || detail.FromDate,
            )
            if (!detailStart || !detailEnd) return

            const overlapDays = countRangeOverlap(
                detailStart,
                detailEnd,
                weekStart,
                weekEnd,
            )
            if (overlapDays <= 0) return

            const fromDistance = Math.abs(
                detailStart.getTime() - weekStart.getTime(),
            )
            const score = overlapDays * 100000 - fromDistance

            if (score > bestScore) {
                bestScore = score
                bestDetail = detail
                bestDetailIndex = index
            }
        })

        if (bestDetailIndex >= 0) {
            usedDetailIndexes.add(bestDetailIndex)
        }

        return bestDetail
    })
}
